import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import type { UserRole } from "@/types/auth";

interface ProtectedRouteProps {
  allowedRoles?: UserRole[];
}

/**
 * Wraps admin routes.
 * - Not authenticated  → /admin/login
 * - Wrong role         → /admin (dashboard, with least privilege)
 * - Loading            → blank screen (brief, auth resolves fast)
 */
export default function ProtectedRoute({ allowedRoles }: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    // Keep it minimal — just a full-screen green bg while auth resolves.
    // A spinner component will replace this in a later phase.
    return (
      <div className="flex min-h-screen items-center justify-center bg-metro-green-950">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/20 border-t-white" />
      </div>
    );
  }

  if (!user) {
    // Preserve the attempted URL so we can redirect back after login
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Authenticated but wrong role — send to dashboard
    return <Navigate to="/admin" replace />;
  }

  return <Outlet />;
}